import React, { useRef, useState, useCallback } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Link } from 'react-router-dom';
import AnimatedArrow from './AnimatedArrow';

interface MagneticButtonProps {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
  to?: string;
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
  showArrow?: boolean;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
}

// Detect if the primary input is touch (skips magnetic on phones/tablets)
const isTouchDevice = () =>
  typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches;

const MagneticButton: React.FC<MagneticButtonProps> = ({
  children,
  className = '',
  onClick,
  to,
  variant = 'primary',
  showArrow = false,
  type = 'button',
  disabled = false,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);
  const prefersReduced = useReducedMotion();
  const touch = isTouchDevice();

  // Only run magnetic tracking on pointer devices and when motion is OK
  const handleMouse = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (touch || prefersReduced) return;
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const middleX = e.clientX - (rect.left + rect.width  / 2);
    const middleY = e.clientY - (rect.top  + rect.height / 2);
    setPosition({ x: middleX * 0.12, y: middleY * 0.12 });
  }, [touch, prefersReduced]);

  const reset = useCallback(() => {
    setPosition({ x: 0, y: 0 });
    setIsHovered(false);
  }, []);

  // ── Styles ──────────────────────────────────────────────────────────────────
  const base =
    'magnetic-btn relative px-6 py-3 sm:px-8 sm:py-3.5 rounded-full font-semibold text-sm sm:text-base ' +
    'transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer ' +
    'overflow-hidden select-none no-underline min-h-[44px]';

  const variants: Record<string, string> = {
    primary:   'bg-gradient-to-r from-[#2563eb] to-[#10b981] text-white hover:shadow-[0_0_28px_rgba(59,130,246,0.55)] border-none',
    secondary: 'bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-white/20',
    outline:   'bg-transparent border border-[#2563eb] text-[#2563eb] hover:bg-[#2563eb]/10',
    ghost:     'bg-transparent text-slate-300 hover:text-white',
  };

  const content = (
    <motion.div
      ref={ref}
      className={`${base} ${variants[variant]} ${className} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      onMouseMove={handleMouse}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={reset}
      animate={!touch && !prefersReduced ? { x: position.x, y: position.y } : { x: 0, y: 0 }}
      transition={{ type: 'spring', stiffness: 180, damping: 18, mass: 0.1 }}
      onClick={disabled ? undefined : onClick}
      whileTap={!disabled ? { scale: 0.97 } : {}}
    >
      <span className="relative z-10 flex items-center gap-2">
        {children}
        {showArrow && <AnimatedArrow isHovered={isHovered} className="ml-0.5 w-4 h-4" />}
      </span>

      {/* Glossy shimmer — primary only */}
      {variant === 'primary' && (
        <div className="absolute inset-0 bg-gradient-to-tr from-white/15 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-400 pointer-events-none" />
      )}
    </motion.div>
  );

  if (to && !disabled) {
    return (
      <Link to={to} className="no-underline" tabIndex={-1} aria-disabled={disabled}>
        {content}
      </Link>
    );
  }

  return (
    <button type={type} disabled={disabled} className="contents" onClick={disabled ? undefined : onClick}>
      {content}
    </button>
  );
};

export default MagneticButton;
